// src/data/rates.ts — exchange rates: stored, fetched, and typed by hand.
//
// Non-negotiable #5: "Manual FX override must stay fully usable. The trip is
// behind the GFW on a VPN and the rate fetch will sometimes fail. A hand-typed
// rate is a first-class path, not a degraded fallback."
//
// So the order of precedence is deliberately *not* "live rate, falling back to
// whatever we have". It is:
//
//   1. Whatever is in fx_rates for that pair, most recent as_of first. That
//      includes rates a person typed in the Group screen — once Edris types a
//      rate on the VPN, everyone on the trip gets it, because it is a row in
//      the shared database rather than a value in one browser.
//   2. A live fetch, only when asked for explicitly, and only to write a row
//      into (1).
//   3. A hardcoded seed, clearly labelled as a guess, never applied silently.
//
// The sign convention, restated because it is the easiest thing here to invert:
//   rate = how many units of the trip's base currency one unit of the expense
//   currency buys.  base_amount = amount * rate.
// (base CAD, quote CNY, rate 0.19) means ¥1 = $0.19 CAD.

import { supabase } from '../lib/supabase';
import { run, unwrap } from '../lib/errors';
import type { FxRateRow } from './types';

export type RateSource = 'client' | 'provider' | 'seed';

export interface KnownRate {
  rate: number;
  asOf: string;
  source: RateSource;
}

/**
 * The contract's build order, step 5: "fx-rates — until it exists, hardcode a
 * CNY rate and move on."
 *
 * This is that hardcoded rate, and it is scoped as narrowly as possible: one
 * pair, the trip that is actually happening. It is never written to fx_rates
 * and never used without the UI saying where it came from, because
 * rate_at_entry is frozen at insert and never recomputed — an expense saved
 * against a stale guess stays wrong for the life of the trip.
 */
export const SEED_RATES: Record<string, Record<string, number>> = {
  CAD: { CNY: 0.19 },
};

export function seedRate(base: string, quote: string): number | null {
  return SEED_RATES[base]?.[quote] ?? null;
}

/**
 * Every pair we have a stored rate for, keyed by quote currency, keeping the
 * most recent as_of for each.
 *
 * Ordered by as_of descending and reduced by first-seen, rather than asking
 * Postgres for a DISTINCT ON — PostgREST cannot express that, and the table
 * holds one row per pair per day, so this is a few dozen rows at most.
 */
export async function loadRates(baseCurrency: string): Promise<Map<string, KnownRate>> {
  const rows = await unwrap<FxRateRow[]>(
    supabase
      .from('fx_rates')
      .select('*')
      .eq('base_currency', baseCurrency)
      .order('as_of', { ascending: false }),
  );

  const latest = new Map<string, KnownRate>();
  for (const row of rows) {
    if (!latest.has(row.quote_currency)) {
      latest.set(row.quote_currency, {
        rate: Number(row.rate),
        asOf: row.as_of,
        source: row.source,
      });
    }
  }
  return latest;
}

/**
 * The rate to use for an expense in `quote`, and where it came from.
 * A base-currency expense is 1:1 and needs no lookup.
 */
export function resolveRate(
  base: string,
  quote: string,
  known: Map<string, KnownRate>,
): KnownRate | null {
  if (base === quote) return { rate: 1, asOf: '', source: 'provider' };

  const stored = known.get(quote);
  if (stored) return stored;

  const seed = seedRate(base, quote);
  return seed == null ? null : { rate: seed, asOf: '', source: 'seed' };
}

export async function saveRate(
  base: string,
  quote: string,
  rate: number,
  source: 'client' | 'provider' = 'client',
): Promise<void> {
  await run(
    supabase.rpc('set_fx_rate', {
      p_base: base,
      p_quote: quote,
      p_rate: rate,
      p_as_of: new Date().toISOString().slice(0, 10),
      p_source: source,
    }),
  );
}

/** How long to wait on the rates provider before giving up and moving on. */
const FETCH_TIMEOUT_MS = 8000;

export class RateFetchError extends Error {}

/**
 * Fetches live rates from Frankfurter (free, ECB data, no key) — the contract
 * names it as one of the two acceptable providers.
 *
 * Frankfurter quotes the other direction from us: ?from=CAD&to=CNY answers
 * "1 CAD = 5.2 CNY". We want "1 CNY = ? CAD", so every rate is inverted on the
 * way in. That inversion is the reason this function exists rather than the
 * caller fetching the URL itself.
 *
 * The timeout is not decoration. Behind the GFW a blocked host does not refuse
 * the connection, it hangs, and a rate refresh that never settles is worse
 * than one that fails — the manual path is right there.
 */
export async function fetchLiveRates(
  base: string,
  quotes: string[],
): Promise<Map<string, number>> {
  const wanted = [...new Set(quotes)].filter((q) => q && q !== base);
  if (wanted.length === 0) return new Map();

  const url = `https://api.frankfurter.app/latest?from=${encodeURIComponent(base)}&to=${encodeURIComponent(wanted.join(','))}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      throw new RateFetchError(`The rates service answered ${response.status}.`);
    }

    const body = (await response.json()) as { rates?: Record<string, number> };
    const out = new Map<string, number>();

    for (const [quote, perBase] of Object.entries(body.rates ?? {})) {
      const value = Number(perBase);
      if (Number.isFinite(value) && value > 0) {
        // Invert: they gave us quote-per-base, we store base-per-quote.
        out.set(quote.toUpperCase(), 1 / value);
      }
    }

    if (out.size === 0) throw new RateFetchError('The rates service returned nothing usable.');
    return out;
  } catch (error) {
    if (error instanceof RateFetchError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new RateFetchError('The rates service did not answer in time.');
    }
    throw new RateFetchError('Could not reach the rates service.');
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetch and store in one go, which is what the Group screen's refresh button
 * does. Rates that come back are written to fx_rates as `provider` rows so
 * everyone else on the trip gets them too — including whoever's VPN is down.
 */
export async function refreshRates(base: string, quotes: string[]): Promise<number> {
  const fetched = await fetchLiveRates(base, quotes);
  for (const [quote, rate] of fetched) {
    await saveRate(base, quote, Number(rate.toFixed(8)), 'provider');
  }
  return fetched.size;
}
